import React, { createContext } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { isNil } from 'lodash';
import classNames from 'classnames';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Tooltip from 'components/tooltips/Tooltip';


const MetricContext = createContext();

const useStyles = makeStyles(theme => ({
  label: {
    fontSize: '0.875rem',
    display: 'flex',
    gap: '0.25rem',
    marginBottom: '0.625rem',
  },
  value: {
    fontWeight: 'bold',
    marginTop: '0.063rem',
  },
  infoBarWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1.25rem',
  },
  barWrapper: {
    display: 'flex',
    gap: '0.125rem',
    width: '100%',
  },
  infoIcon: {
    width: '1.125rem',
    height: '1.125rem',
    color: theme.palette.secondary['300'],
    cursor: 'pointer',
  },
  innerBarWrapper: {
    position: 'relative',
    flex: props => props.weight,
  },
  innerBar: {
    height: '0.5rem',
    position: 'relative',
    zIndex: 1,
    borderRadius: '2px',
  },
  good: {
    backgroundColor: theme.palette.primary.main,
  },
  neutral: {
    backgroundColor: theme.palette.accent2['1000'],
  },
  bad: {
    backgroundColor: theme.palette.accent3.main,
  },
  valueMark: {
    width: '0.5rem',
    height: '1.5rem',
    background: theme.palette.secondary.text,
    position: 'absolute',
    transform: 'translateY(-50%)',
    top: '50%',
    borderRadius: '0.5rem',
    border: '2px solid white',
    zIndex: 2,
  },
}));

const MetricBar = ({ children, ...restProps }) => {
  const {
    label, value, unit = '', info,
  } = restProps;
  const classes = useStyles(restProps);

  return (
    <MetricContext.Provider value={restProps}>
      <div>
        <div className={classes.label}>{label}:
          {isNil(value) ?
            <div className="loading-dots" /> :
            <span className={classes.value}>{value} {unit}</span>
          }
        </div>
        <div className={classes.infoBarWrapper}>
          <div className={classes.barWrapper}>{children}</div>
          <Tooltip title={info} interactive>
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
        </div>
      </div>
    </MetricContext.Provider>
  );
};

function getMarkerPosition(value, min, max) {
  switch (true) {
    case isNil(value):
      return null;
    case value >= min && value <= max:
      return `${((value - min) / (max - min)) * 100}%`;
    case min === max && value === min:
      return '50%';
    case value >= min && isNil(max):
      return '50%';
    default:
      return null;
  }
}


MetricBar.InnerBar = ({ children, ...restProps }) => {
  const classes = useStyles(restProps);
  const {
    min, max, color,
  } = restProps;

  return (
    <MetricContext.Consumer>
      {({ value, tooltip }) => {
        const markerPos = getMarkerPosition(value, min, max);
        return (
          <div className={classes.innerBarWrapper}>
            {!isNil(markerPos) && (
              <Tooltip title={tooltip}>
                <div className={classes.valueMark} style={{ left: markerPos }} />
              </Tooltip>
            )}
            <div
              className={classNames(classes.innerBar, classes[color])}
              style={{ opacity: markerPos ? 1 : 0.4 }}
            >
              {children}
            </div>
          </div>
        );
      }}
    </MetricContext.Consumer>
  );
};

MetricBar.colors = {
  good: 'good',
  neutral: 'neutral',
  bad: 'bad',
};

MetricBar.propTypes = {
  children: PropTypes.node.isRequired,
};

MetricBar.InnerBar.propTypes = {
  children: PropTypes.node.isRequired,
};

export default MetricBar;
